import {get, isNil} from 'lodash'
import {isExpired} from 'helpers/user'
import {ONE_DAY_MILLISECONDS} from 'helpers/dates'

export const SetupIntentStatus = {
  UNKNOWN: 'UNKNOWN',
  SUCCESS: 'SUCCESS',
  PROCESSING: 'PROCESSING',
  REQUIRES_PAYMENT_METHOD: 'REQUIRES_PAYMENT_METHOD',
  ERROR: 'ERROR',
}

export const SubscriptionStatus = {
  INCOMPLETE: 'INCOMPLETE',
  INCOMPLETE_EXPIRED: 'INCOMPLETE_EXPIRED',
  TRIALING: 'TRIALING',
  ACTIVE: 'ACTIVE',
  PAST_DUE: 'PAST_DUE',
  CANCELED: 'CANCELED',
  UNPAID: 'UNPAID',
}

export const SubscriptionBillingPeriod = {
  MONTHLY: 'MONTHLY',
  YEARLY: 'YEARLY',
}

export const PaymentType = {
  CARD: 'CARD',
  DIRECT_DEBIT: 'DIRECT_DEBIT',
  OTHER: 'OTHER',
  NONE: 'NONE',
}

export const getTotalDue = (user, planPrices, estimatedInvoice, billingPeriod) => {
  if (isFullAmountDue(user)) {
    return isBillingPeriodYearly(billingPeriod)
        ? get(planPrices, 'yearlyPriceIncludingTax', 0)
        : get(planPrices, 'monthlyPriceIncludingTax', 0)
  }

  return getTotalDueFromEstimatedInvoice(estimatedInvoice)
}

export const isFullAmountDue = (user) => {
  return isExpired(user)
}

export const getTotalDueFromEstimatedInvoice = (invoice) => {
  return !isNil(invoice) && invoice.priceIncludingTax > 0 && isEstimatedInvoiceProration(invoice)
      ? invoice.priceIncludingTax
      : 0
}

export const isEstimatedInvoiceProration = (invoice) => {
  return invoice.items.some(isEstimatedInvoiceItemProration)
}

export const isEstimatedInvoiceItemProration = (invoiceItem) => {
  return invoiceItem.proration
}

export const getNextBillingDate = (accountPackages, estimatedInvoice) => {
  return isNil(estimatedInvoice) || isEstimatedInvoiceProration(estimatedInvoice)
      ? null
      : estimatedInvoice.date
}

export const isPlanPriceHasTax = (priceExcludingTax, priceIncludingTax) => {
  return priceIncludingTax > priceExcludingTax
}

export const getPlanPriceExcludingTax = (planPrices, isYearly) => {
  return isYearly
      ? planPrices.yearlyPriceExcludingTax
      : planPrices.monthlyPriceExcludingTax
}

export const getPlanPriceIncludingTax = (planPrices, isYearly) => {
  return isYearly
      ? planPrices.yearlyPriceIncludingTax
      : planPrices.monthlyPriceIncludingTax
}

export const getPaymentFailureMessage = (supportPhone) => {
  return `Sorry, there was an issue accepting your payment. Please try an alternative payment method or contact us on ${supportPhone}`
}

export const getGenericUpdateFailureMessage = (supportPhone) => {
  return `To update your plan, please contact your account manager on ${supportPhone}`
}

export const getUserUpdateFailureMessage = () => {
  return 'Sorry, there was an issue sending your invitations. Please re-add new users from the Users area.'
}

export const getYearlyToMonthlyMessage = (supportPhone) => {
  return `To change from a yearly to a monthly plan, please contact us on ${supportPhone}.`
}

export const getBillingPeriodFromInvoice = (invoice) => {
  return getBillingPeriod(invoice.periodStartDate, invoice.periodEndDate)
}

export const getBillingPeriod = (periodStart, periodEnd) => {
  const periodInDays = (periodEnd - periodStart) / ONE_DAY_MILLISECONDS
  return periodInDays > 31
      ? SubscriptionBillingPeriod.YEARLY
      : SubscriptionBillingPeriod.MONTHLY
}

export const isBillingPeriodYearly = (billingPeriod) => {
  return billingPeriod === SubscriptionBillingPeriod.YEARLY
}

export const getBillingPeriodLabel = (subscriptionBillingPeriod) => {
  return subscriptionBillingPeriod === SubscriptionBillingPeriod.MONTHLY
      ? "Monthly"
      : "Yearly"
}

export const getBillingPeriodPerString = (billingPeriod) => {
  return billingPeriod === SubscriptionBillingPeriod.MONTHLY
      ? '/month'
      : '/year'
}

export const isSubscriptionPaymentRequiresAction = (subscription) => {
  return get(subscription, 'paymentRequiresAction', false)
}

export const isChangingYearlyToMonthly = (currentBillingPeriod, selectedBillingPeriod) => {
  return currentBillingPeriod === 'YEARLY' && selectedBillingPeriod === 'MONTHLY'
}

export const canManagePlan = (user) => {
  return user.paymentType !== PaymentType.DIRECT_DEBIT && user.paymentType !== PaymentType.OTHER
}
